import React from "react";
import { View, KeyboardAvoidingView, FlatList, Text } from "react-native";

import Utils, {
  hasItems,
  isAndroid,
  escapeRegexp,
  navigate,
  hasOneItem,
} from "../../../helpers/utils";
import appStyles from "../../appStyles";

import Search from "../common/search-bar/Search";
import Header from "../common/header/index";

import IntegrationWrapper from "./details/IntegrationWrapper";
import IntegrationsHelper from "./IntegrationsHelper";
import IntegrationsHistoryFabButton from "./IntegrationsHistoryFabButton";
import IntegrationIcons from "../common/icons/integrations/index";
import RoundCheckbox from "../common/RoundCheckbox";
import integrationStyles from "./details/integrationStyles";
import appPages from "../../../appPages";
import DeleteRuleConfirmDialog from "./delete/DeleteRuleConfirmDialog";
import IntegrationMenuOptions from "./common/IntegrationMenuOptions";
import MoodColors from "../common/mood/MoodColors";

export default class IntegrationsHistoryPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    this.state = {
      integrations: [],
      filteredIntegrations: [],
      selectedIntegrationsHash: {},
      selectedIds: new Set([]),
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.iniIntegrations();
        },
      );
    } else {
      this.iniIntegrations();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  iniIntegrations = () => {
    const pageLastKnownTime = null;
    const pageLastKnownId = null;
    IntegrationsHelper.getIntegrationDataBySource(
      this.controller,
      null,
      pageLastKnownTime,
      pageLastKnownId,
    ).then((integrations) => {
      this.setState((state) => ({
        ...state,
        integrations,
        filteredIntegrations: integrations,
      }));
    });
  };

  goBack = () => {
    this.controller.goBack();
  };

  // ------------------------------------- FILTER -------------------------------------
  filterByKeyword = ({ term }) => {
    const { integrations } = this.state;

    const pattern = Utils.getSearchPattern(term);
    const filteredIntegrations = term
      ? integrations.filter((a) => {
          return a.name && a.name.match(pattern);
        })
      : integrations;

    this.setState((state) => ({
      ...state,
      isLoading: false,
      filteredIntegrations,
    }));
  };

  enableEditMode = () => {
    this.setState((state) => ({
      ...state,
      isEditMode: !this.state.isEditMode,
    }));
  };

  viewRules = () => {
    navigate(this.controller, appPages.integrationRulesPage);
  };

  // ------------------------------------- SELECTION -------------------------------------
  isSelected = (integrationId) => {
    const { selectedIds } = this.state;
    return selectedIds.has(integrationId);
  };

  toggleAll = (isSelected) => {
    const { integrations } = this.state;

    let selectedIntegrationsHash = {};
    if (!isSelected) {
      integrations.forEach((int) => {
        selectedIntegrationsHash[int.id] = int;
      });
    }

    this.setState((state) => ({
      ...state,
      selectedIntegrationsHash,
      selectedIds: new Set(Object.keys(selectedIntegrationsHash)),
    }));
  };

  toggleIntegrationSelection = (integration, is_selected) => {
    const { selectedIntegrationsHash } = this.state;

    if (!integration.id) return;

    if (!is_selected) {
      selectedIntegrationsHash[integration.id] = integration;
    } else {
      delete selectedIntegrationsHash[integration.id];
    }

    this.setState((state) => ({
      ...state,
      selectedIntegrationsHash,
      selectedIds: new Set(Object.keys(selectedIntegrationsHash)),
    }));
  };

  isValidModel = () => {
    const { selectedIds } = this.state;
    return selectedIds.size > 0;
  };

  viewIgnored = () => {
    navigate(this.controller, appPages.integrationRulesPage);
  };

  // ------------------------------------- DIALOGS -------------------------------------
  toggleDeleteRuleDialog = (integration) => {
    // If integrations has rule show rule dialog
    if (!integration || integration.rule) {
      this.setState((state) => ({
        ...state,
        selectedRule: integration,
      }));
      // Show delete instance dialog
    } else {
      this.setState((state) => ({
        ...state,
        selectedIntegrations: [integration],
      }));
    }
  };

  showDeleteIntegrationDialog = () => {
    const { selectedIds, filteredIntegrations } = this.state;

    const selectedIntegrations = filteredIntegrations.filter((int) =>
      selectedIds.has(int.id),
    );

    this.setState((state) => ({
      ...state,
      selectedIntegrations,
    }));
  };

  closeDeleteIntegrationDialog = () => {
    this.setState((state) => ({
      ...state,
      selectedIntegrations: null,
    }));
  };

  ignore = () => {
    const { selectedIntegrationsHash } = this.state;

    this.closeDeleteIntegrationDialog();
    this.toggleDeleteRuleDialog(null);

    const rulesToIgnore = Object.values(
      selectedIntegrationsHash,
    ).map((int) => ({ ...int.rule, id: int.matching_rule }));
    if (!hasItems(rulesToIgnore)) return;

    IntegrationsHelper.stopGathering(this.controller, rulesToIgnore).then(
      () => {
        // Go to rules page
        navigate(this.controller, appPages.integrationRulesPage, "changePage", {
          selectedIds: rulesToIgnore.map((r) => r.id),
          tab: "ignore",
        });
      },
    );
  };

  goToIgnorePage = (integration) => {
    if (!integration) return;

    // Go to rules home page
    if (hasOneItem(integration.rules)) {
      IntegrationsHelper.stopGathering(this.controller, [
        { ...integration.rule, id: integration.matching_rule },
      ]).then(() => {
        navigate(this.controller, appPages.integrationRulesPage, "changePage", {
          selectedIds: [integration.matching_rule],
          tab: "ignore",
        });
      });

      // Multiple rules => go to setup rule
    } else {
      navigate(this.controller, appPages.integrationsIgnorePage, "changePage", {
        integrations: [integration],
      });
    }
  };

  deleteRule = () => {
    const { selectedRule } = this.state;

    IntegrationsHelper.deleteRule(this.controller, selectedRule.rule).then(
      () => {
        this.toggleDeleteRuleDialog(null);
        this.iniIntegrations();
      },
    );
  };

  deleteIntegrations = () => {
    const { selectedIds } = this.state;

    this.closeDeleteIntegrationDialog();
    if (selectedIds.size == 0) return;

    IntegrationsHelper.deleteEntries(
      this.controller,
      Array.from(selectedIds),
    ).then(() => {
      setTimeout(() => {
        this.iniIntegrations();
      }, 1000);
    });
  };

  // ------------------------------------- LONG PRESS MENU -------------------------------------
  toggleMenuIntegration = (selectedMenuIntegration) => {
    this.setState((state) => ({ ...state, selectedMenuIntegration }));
  };

  // ------------------------------------- RENDER -------------------------------------
  render() {
    const {
      filteredIntegrations,
      isLoading,
      selectedIds,
      isEditMode,
      selectedMenuIntegration,
      selectedIntegrations,
      selectedRule,
    } = this.state;

    const allSelected =
      hasItems(filteredIntegrations) &&
      filteredIntegrations.length == selectedIds.size;
    const hasChildrenChecked = !allSelected && selectedIds.size > 0;

    const ListHeader = (
      <View style={integrationStyles.listHeader}>
        <Text style={integrationStyles.listHeaderText}>
          All Integration Data
        </Text>
        {isEditMode ? (
          <RoundCheckbox
            size={16}
            checked={allSelected}
            hasChildrenChecked={hasChildrenChecked}
            onValueChange={() => this.toggleAll(allSelected)}
          />
        ) : null}
      </View>
    );

    return (
      <View style={[appStyles.container, { backgroundColor: "#F3F2F1" }]}>
        <KeyboardAvoidingView
          style={[appStyles.kbViewNoFooter]}
          behavior="position"
          keyboardVerticalOffset={isAndroid ? 30 : 0}
        >
          <Header
            title="Integration Data History"
            subTitle="Logs of integration data"
            isLoading={isLoading}
            goBack={this.goBack}
            iconTag={<IntegrationIcons.RulesIcon onPress={this.viewRules} />}
            iconTag2={
              <IntegrationIcons.EditWhitePencil
                width={20}
                height={20}
                onPress={this.enableEditMode}
              />
            }
          />

          {/* Integrations */}
          {hasItems(filteredIntegrations) ? (
            <FlatList
              contentContainerStyle={{ paddingBottom: 100 }}
              style={integrationStyles.flatList}
              data={filteredIntegrations}
              keyExtractor={(item) => item.id}
              ListHeaderComponent={ListHeader}
              renderItem={({ item }) => (
                <IntegrationWrapper
                  data={item}
                  controller={this.controller}
                  isSelected={this.isSelected(item.id)}
                  showDate
                  hasRules={!isEditMode}
                  hasDropdownArrow={!isEditMode}
                  showRulesSummary
                  showSelector
                  style={{ paddingRight: 0 }}
                  onLongPress={() => this.toggleMenuIntegration(item)}
                  deleteIntegration={this.toggleDeleteRuleDialog}
                  ignoreIntegration={this.goToIgnorePage}
                  onToggle={this.toggleIntegrationSelection}
                />
              )}
            />
          ) : null}

          <View style={[appStyles.footerOptions]}>
            <Search
              showIcon={false}
              controller={this.controller}
              placeholder="Search Data"
              filterResults={this.filterByKeyword}
              buttonOptions={
                isEditMode
                  ? [
                      {
                        disabled: !this.isValidModel(),
                        style: [
                          integrationStyles.submitButton,
                          { marginRight: 6 },
                        ],
                        styleText: [
                          integrationStyles.submitButtonText,
                          {
                            color: MoodColors.VeryBad,
                          },
                        ],
                        text: "Ignore",
                        onPress: () => this.ignore(),
                      },
                      {
                        disabled: !this.isValidModel(),
                        style: [
                          integrationStyles.submitButton,
                          { backgroundColor: MoodColors.VeryBad },
                        ],
                        styleText: [
                          integrationStyles.submitButtonText,
                          { color: "#FFF" },
                        ],
                        text: "Delete",
                        onPress: this.showDeleteIntegrationDialog,
                        isMainButton: true,
                      },
                    ]
                  : null
              }
            />
          </View>
        </KeyboardAvoidingView>
        <View style={[appStyles.footerOptions]}>
          <IntegrationsHistoryFabButton
            style={isEditMode ? { bottom: 70 } : {}}
            viewIgnored={this.viewIgnored}
          />
        </View>

        {/* Delete rule confirm dialog */}
        {selectedRule ? (
          <DeleteRuleConfirmDialog
            data={[selectedRule]}
            entity="rule"
            controller={this.controller}
            ignore={() => this.ignore()}
            cancel={() => this.toggleDeleteRuleDialog(null)}
            confirm={this.deleteRule}
          />
        ) : null}

        {/* Delete integrations confirm dialog */}
        {hasItems(selectedIntegrations) ? (
          <DeleteRuleConfirmDialog
            data={selectedIntegrations}
            controller={this.controller}
            ignore={this.ignore}
            cancel={this.closeDeleteIntegrationDialog}
            confirm={this.deleteIntegrations}
          />
        ) : null}

        <IntegrationMenuOptions
          controller={this.controller}
          integration={selectedMenuIntegration}
          close={() => this.toggleMenuIntegration(null)}
        />
      </View>
    );
  }
}
